// Small shared UI building blocks. Plain StyleSheet; the only animation is the fades in Motion.tsx.
import React from 'react';
import {
  ActivityIndicator, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View,
  type ScrollViewProps, type StyleProp, type TextInputProps, type TextStyle, type ViewStyle,
} from 'react-native';
import Icon, { type IconName } from './Icon';
import DietMark from './DietMark';
import { Fade } from './Motion';
import { useLabels } from '../labels';
import { font, radius, spacing, usePalette, type Palette } from '../theme';
import type { DietFlags } from '../logic/diets';

export function useStyles<T>(factory: (p: Palette) => T): T {
  const p = usePalette();
  return React.useMemo(() => factory(p), [p, factory]);
}

/** Scrollable page with the standard padding and background. */
export function Screen({ children, contentStyle, ...rest }: ScrollViewProps & { contentStyle?: StyleProp<ViewStyle> }) {
  const p = usePalette();
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: p.background }}
      contentContainerStyle={[{ padding: spacing.l, gap: spacing.l, paddingBottom: spacing.xxxl * 2 }, contentStyle]}
      keyboardShouldPersistTaps="handled"
      {...rest}>
      {children}
    </ScrollView>
  );
}

export function Title({ children, style }: { children: React.ReactNode; style?: StyleProp<TextStyle> }) {
  const p = usePalette();
  return <Text style={[{ fontSize: font.title, fontWeight: '700', color: p.text }, style]}>{children}</Text>;
}

export function Body({ children, style, secondary, numberOfLines }: { children: React.ReactNode; style?: StyleProp<TextStyle>; secondary?: boolean; numberOfLines?: number }) {
  const p = usePalette();
  return (
    <Text numberOfLines={numberOfLines} style={[{ fontSize: font.body, color: secondary ? p.secondaryText : p.text, lineHeight: 22 }, style]}>
      {children}
    </Text>
  );
}

export function Caption({ children, style, color }: { children: React.ReactNode; style?: StyleProp<TextStyle>; color?: string }) {
  const p = usePalette();
  return <Text style={[{ fontSize: font.footnote, color: color ?? p.secondaryText }, style]}>{children}</Text>;
}

/** `iconOnly`: the title is just a name for the icon, so it hides unless labels are on. */
export function Section({ title, icon, children, right, style, iconOnly }: { title?: string; icon?: IconName; children?: React.ReactNode; right?: React.ReactNode; style?: StyleProp<ViewStyle>; iconOnly?: boolean }) {
  const p = usePalette();
  const words = useLabels() || !iconOnly || !icon;
  return (
    <View style={[{ gap: spacing.s }, style]}>
      {title ? (
        <View accessibilityRole="header" accessibilityLabel={title} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.s }}>
          {icon ? <Icon name={icon} size={words ? 20 : 24} color={words ? p.secondaryText : p.tint} /> : null}
          {words ? <Text style={{ flex: 1, fontSize: font.title3, fontWeight: '700', color: p.text }}>{title}</Text> : <View style={{ flex: 1 }} />}
          {right}
        </View>
      ) : null}
      {children}
    </View>
  );
}

export function SectionHeader({ title, color }: { title: string; color?: string }) {
  const p = usePalette();
  return (
    // 600, not bold: small capitals stay in the text face (a script face would make them unreadable).
    <Text style={{ fontSize: font.caption, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: color ?? p.secondaryText, marginTop: spacing.s }}>
      {title}
    </Text>
  );
}

type ButtonProps = {
  label: string;
  onPress: () => void;
  icon?: IconName;
  variant?: 'primary' | 'secondary' | 'plain' | 'danger' | 'super';
  disabled?: boolean;
  big?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  /** The label only names the icon: hidden unless labels are on. */
  iconOnly?: boolean;
};

export function Button({ label, onPress, icon, variant = 'primary', disabled, big, loading, style, accessibilityLabel, iconOnly }: ButtonProps) {
  const p = usePalette();
  const words = useLabels() || !iconOnly || !icon;
  const bg = { primary: p.tint, secondary: p.card, plain: 'transparent', danger: p.danger, super: p.super }[variant];
  const fg = variant === 'secondary' || variant === 'plain' ? (variant === 'plain' ? p.tint : p.text) : p.onTint;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      disabled={disabled || loading}
      onPress={onPress}
      style={({ pressed }) => [
        {
          flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.s, backgroundColor: bg,
          minHeight: big ? 64 : 48, minWidth: words ? undefined : big ? 64 : 48, paddingHorizontal: words ? (big ? spacing.xl : spacing.l) : spacing.m, borderRadius: radius.l,
          opacity: disabled ? 0.4 : pressed ? 0.7 : 1,
        },
        style,
      ]}>
      {loading ? <ActivityIndicator color={fg} /> : icon ? <Icon name={icon} color={fg} size={big ? (words ? 26 : 30) : words ? 20 : 22} /> : null}
      {words ? <Text style={{ color: fg, fontSize: big ? font.title3 : font.body, fontWeight: '600' }}>{label}</Text> : null}
    </Pressable>
  );
}

export function IconButton({ icon, onPress, label, color, size = 24, disabled }: { icon: IconName; onPress: () => void; label: string; color?: string; size?: number; disabled?: boolean }) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      hitSlop={10}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => ({ padding: spacing.xs, opacity: disabled ? 0.3 : pressed ? 0.5 : 1 })}>
      <Icon name={icon} color={color} size={size} />
    </Pressable>
  );
}

/**
 * Tappable list row: leading icon/thumbnail, title, subtitle, trailing content.
 * `diets`: diet marks (red: certainly breaks, orange: unconfirmed warning); the title turns secondary.
 */
export function ListItem({
  title, subtitle, icon, iconColor, leading, right, onPress, onLongPress, chevron = !!onPress, selected, style, diets,
}: {
  title: string; subtitle?: string; icon?: IconName; iconColor?: string; leading?: React.ReactNode; right?: React.ReactNode;
  onPress?: () => void; onLongPress?: () => void; chevron?: boolean; selected?: boolean; style?: StyleProp<ViewStyle>; diets?: DietFlags;
}) {
  const muted = !!diets && (diets.breaks.length > 0 || diets.unsure.length > 0);
  const p = usePalette();
  const lead = leading ?? (icon ? <Icon name={icon} color={iconColor ?? p.tint} /> : null);
  return (
    <Pressable
      accessibilityRole={onPress ? 'button' : undefined}
      onPress={onPress}
      onLongPress={onLongPress}
      disabled={!onPress && !onLongPress}
      style={({ pressed }) => [
        { flexDirection: 'row', alignItems: 'center', gap: spacing.m, paddingVertical: spacing.m, paddingHorizontal: spacing.l, backgroundColor: p.card, borderRadius: radius.l, minHeight: 56, opacity: pressed ? 0.7 : 1 },
        style,
      ]}>
      {lead ? (
        // Selecting puts the circle on the leading icon's spot (over a thumbnail), so nothing shifts.
        <View>
          <Fade visible={selected === undefined || !!leading}>{lead}</Fade>
          <Fade visible={selected !== undefined} style={[StyleSheet.absoluteFill, { alignItems: 'center', justifyContent: 'center' }]}>
            <View style={leading ? { backgroundColor: p.card, borderRadius: radius.pill } : undefined}>
              <Icon name={selected ? 'checkCircle' : 'circle'} color={selected ? p.success : p.secondaryText} />
            </View>
          </Fade>
        </View>
      ) : selected !== undefined ? (
        <Icon name={selected ? 'checkCircle' : 'circle'} color={selected ? p.success : p.secondaryText} />
      ) : null}
      <View style={{ flex: 1, gap: 2 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.s }}>
          <Text style={{ flexShrink: 1, fontSize: font.body, fontWeight: muted ? '400' : '600', color: muted ? p.secondaryText : p.text }} numberOfLines={2}>{title}</Text>
          {muted ? <DietMark flags={diets} /> : null}
        </View>
        {subtitle ? <Text style={{ fontSize: font.footnote, color: p.secondaryText }} numberOfLines={2}>{subtitle}</Text> : null}
      </View>
      {right}
      {chevron ? <Icon name="forward" size={18} color={p.secondaryText} /> : null}
    </Pressable>
  );
}

/** `iconOnly`: the label only names the icon, so it hides unless labels are on. */
export function Chip({ label, icon, color, onPress, selected, style, iconOnly }: { label: string; icon?: IconName; color?: string; onPress?: () => void; selected?: boolean; style?: StyleProp<ViewStyle>; iconOnly?: boolean }) {
  const p = usePalette();
  const tint = color ?? p.tint;
  const words = useLabels() || !iconOnly || !icon;
  return (
    <Pressable
      disabled={!onPress}
      onPress={onPress}
      accessibilityLabel={label}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityState={selected !== undefined ? { selected } : undefined}
      style={({ pressed }) => [
        {
          flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: words ? spacing.m : spacing.s + 2, paddingVertical: 6, borderRadius: radius.pill,
          backgroundColor: selected ? tint : p.card, borderWidth: StyleSheet.hairlineWidth, borderColor: selected ? tint : p.border, opacity: pressed ? 0.7 : 1,
        },
        style,
      ]}>
      {icon ? <Icon name={icon} size={words ? 16 : 18} color={selected ? p.onTint : tint} /> : null}
      {words ? <Text style={{ fontSize: font.footnote, fontWeight: '600', color: selected ? p.onTint : p.text }}>{label}</Text> : null}
    </Pressable>
  );
}

/** `readOnly` fades the − and + buttons out but keeps their space. */
export function Stepper({ value, onChange, min = 1, max = 99, label, readOnly = false }: { value: number; onChange: (v: number) => void; min?: number; max?: number; label: string; readOnly?: boolean }) {
  const p = usePalette();
  return (
    <View accessibilityLabel={`${label}: ${value}`} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.s, backgroundColor: p.card, borderRadius: radius.pill, paddingHorizontal: spacing.xs }}>
      <Fade visible={!readOnly}>
        <IconButton icon="minus" label={`${label} −`} onPress={() => onChange(Math.max(min, value - 1))} disabled={value <= min} size={20} />
      </Fade>
      <Text style={{ minWidth: 28, textAlign: 'center', fontSize: font.headline, fontWeight: '700', color: p.text, fontVariant: ['tabular-nums'] }}>{value}</Text>
      <Fade visible={!readOnly}>
        <IconButton icon="plus" label={`${label} +`} onPress={() => onChange(Math.min(max, value + 1))} disabled={value >= max} size={20} />
      </Fade>
    </View>
  );
}

export function Field({ label, style, ...props }: TextInputProps & { label?: string }) {
  const p = usePalette();
  return (
    <View style={{ gap: 6 }}>
      {label ? <Caption>{label}</Caption> : null}
      <TextInput
        placeholderTextColor={p.secondaryText}
        style={[{ backgroundColor: p.card, color: p.text, borderRadius: radius.m, paddingHorizontal: spacing.m, paddingVertical: spacing.m, fontSize: font.body, minHeight: 48 }, style]}
        {...props}
      />
    </View>
  );
}

export function ToggleRow({ label, value, onChange, note }: { label: string; value: boolean; onChange: (v: boolean) => void; note?: string }) {
  const p = usePalette();
  return (
    <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l, paddingVertical: spacing.m, gap: 4 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.m }}>
        <Text style={{ flex: 1, fontSize: font.body, color: p.text }}>{label}</Text>
        <Switch value={value} onValueChange={onChange} trackColor={{ true: p.tint, false: p.border }} />
      </View>
      {note ? <Caption>{note}</Caption> : null}
    </View>
  );
}

/** Pick one option from a short list (wrapping chips). */
export function Choice<T extends string>({ options, value, onChange, label, labels }: { options: readonly T[]; value: T; onChange: (v: T) => void; label?: string; labels: (v: T) => string }) {
  return (
    <View style={{ gap: 6 }}>
      {label ? <Caption>{label}</Caption> : null}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.s }}>
        {options.map(o => (
          <Chip key={o} label={labels(o)} selected={o === value} onPress={() => onChange(o)} />
        ))}
      </View>
    </View>
  );
}

export function EmptyState({ icon, title, body, action }: { icon: IconName; title: string; body?: string; action?: React.ReactNode }) {
  const p = usePalette();
  return (
    <View style={{ alignItems: 'center', gap: spacing.m, paddingVertical: spacing.xxxl, paddingHorizontal: spacing.l }}>
      <Icon name={icon} size={44} color={p.secondaryText} strokeWidth={1.5} />
      <Text style={{ fontSize: font.headline, fontWeight: '700', color: p.text, textAlign: 'center' }}>{title}</Text>
      {body ? <Text style={{ fontSize: font.body, color: p.secondaryText, textAlign: 'center' }}>{body}</Text> : null}
      {action}
    </View>
  );
}

export function ProgressBar({ value, color }: { value: number; color?: string }) {
  const p = usePalette();
  return (
    <View style={{ height: 8, borderRadius: 4, backgroundColor: p.border, overflow: 'hidden' }}>
      <View style={{ width: `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%`, height: 8, backgroundColor: color ?? p.success }} />
    </View>
  );
}

/** A count: "3 of 8 cooked" as words with labels on, else an icon and "3/8". */
export function IconCount({ icon, count, total, text, color }: { icon: IconName; count: number; total?: number; text: string; color?: string }) {
  const p = usePalette();
  if (useLabels()) return <Caption>{text}</Caption>;
  return (
    <View accessibilityLabel={text} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
      <Icon name={icon} size={14} color={color ?? p.secondaryText} />
      <Text style={{ fontSize: font.footnote, fontWeight: '700', color: p.secondaryText, fontVariant: ['tabular-nums'] }}>{total === undefined ? count : `${count}/${total}`}</Text>
    </View>
  );
}

export function Row({ children, gap = spacing.s, style }: { children: React.ReactNode; gap?: number; style?: StyleProp<ViewStyle> }) {
  return <View style={[{ flexDirection: 'row', alignItems: 'center', gap, flexWrap: 'wrap' }, style]}>{children}</View>;
}
